import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { AssignPersonDialogProps } from "../../types/AssignPerson.types";
import PersonSelect from "../person-select/PersonSelect";

const AssignPersonDialog: React.FC<AssignPersonDialogProps> = ({
  open,
  onClose,
  title,
  label,
  currentPersonId,
  persons,
  isLoading: isLoadingPersons = false,
  onAssign,
  allowClear = true,
  clearLabel,
}) => {
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(
    currentPersonId ?? null
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update selected person when currentPersonId changes
  useEffect(() => {
    setSelectedPersonId(currentPersonId ?? null);
  }, [currentPersonId]);

  const handleSave = async () => {
    setIsUpdating(true);
    setError(null);

    try {
      await onAssign(selectedPersonId);
      onClose();
    } catch (err) {
      setError("Kunde inte uppdatera tilldelningen. Försök igen.");
      console.error("Failed to assign person:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setSelectedPersonId(currentPersonId ?? null);
    setError(null);
    onClose();
  };

  const hasChanged = selectedPersonId !== currentPersonId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      {isLoadingPersons ? (
        <DialogContent
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
        >
          <CircularProgress />
        </DialogContent>
      ) : (
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <PersonSelect
            value={selectedPersonId}
            onChange={setSelectedPersonId}
            persons={persons}
            label={label}
            disabled={isUpdating}
            allowClear={allowClear}
            clearLabel={clearLabel}
          />
        </DialogContent>
      )}

      <DialogActions>
        {allowClear && (
          <Button
            variant="text"
            color="warning"
            onClick={() => {
              onAssign(null);
              onClose();
            }}
            disabled={isUpdating}
          >
            Rensa
          </Button>
        )}
        <Button onClick={handleClose} disabled={isUpdating}>
          Avbryt
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUpdating || !hasChanged || isLoadingPersons}
          startIcon={isUpdating ? <CircularProgress size={20} /> : null}
        >
          {isUpdating ? "Sparar..." : "Spara"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPersonDialog;
